













import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Chart } from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import { colors } from '../config';
import KeyValue from '@/utils/KeyValue';
import {
  CHART_WIDTH_BASELINE,
  CompassSlideLayout,
  applyMultiSeriesCompassLayout,
  applyPolarCompassChartSize,
  scaleChartPx,
} from '@/utils/chartPresentationSizing';

More(Highcharts);

@Component({ components: { Chart } })
export default class CompassCompetitorsCqw extends Vue {
  @Prop() readonly rankings!: any[];
  @Prop() readonly overview!: any[];
  @Prop() readonly showBransch!: boolean;
  @Prop({ default: true }) readonly showLabels!: boolean;
  @Prop() containerWidth: any;
  @Prop({ default: true }) readonly dark!: boolean;
  @Prop({ default: 'auto' }) readonly slideLayout!: CompassSlideLayout;

  component: any = undefined;
  hovered: number | null = null;

  chartOptions: any = {
    chart: {
      polar: true,
      type: 'area',
      backgroundColor: 'transparent',
      events: {
        click: this.onClick,
      },
    },

    credits: {
      enabled: false,
    },

    title: {
      text: '',
    },

    pane: {
      size: '80%',
    },

    xAxis: {
      // tslint:disable-next-line:max-line-length
      categories: [
        '<span style="font-weight: 600; color: white;">' +
          Vue.prototype.$t('Avkastning') +
          '</span>',
        Vue.prototype.$t('Rörelsemarginal'),
        Vue.prototype.$t('Tillväxt'),
        '<span  style="font-weight: 600; color: white;">' +
          Vue.prototype.$t('Framtidsutsikter') +
          '</span>',
        Vue.prototype.$t('Soliditet'),
        Vue.prototype.$t('Rörelsekapital i % av omsättning'),
        '<span style="font-weight: 600; color: white;">' +
          Vue.prototype.$t('Finansiell styrka') +
          '</span>',
        Vue.prototype.$t('Kassalikviditet'),
        Vue.prototype.$t('Räntetäckningsgrad'),
        '<span style="font-weight: 600; color: white;">' +
          Vue.prototype.$t('Kortsiktig beredskap') +
          '</span>',
        Vue.prototype.$t('Riskbuffert'),
        Vue.prototype.$t('Räntabilitet på eget kapital'),
      ],
      tickmarkPlacement: 'on',
      lineWidth: 0,
      showEmpty: false,
      plotLines: [
        {
          color: colors.Black,
          width: this.transformPx(4),
          zIndex: 1,
          value: 0,
        },
        {
          color: colors.Black,
          width: this.transformPx(4),
          zIndex: 1,
          value: 3,
        },
        {
          color: colors.Black,
          width: this.transformPx(4),
          zIndex: 1,
          value: 6,
        },
        {
          color: colors.Black,
          width: this.transformPx(4),
          zIndex: 1,
          value: 9,
        },
      ],
      labels: {
        enabled: true,
        style: {
          fontSize: '1cqw',
          color: colors.LightGrey,
        },
        distance: this.transformPx(25),
      },
      gridLineColor: colors.Black,
    },

    yAxis: {
      gridLineInterpolation: 'polygon',
      gridLineDashStyle: 'longdash',
      gridLineColor: colors.Black,
      lineWidth: 0,
      min: 0,
      max: 100,
      tickInterval: 10,
      labels: {
        enabled: false,
      },
      showEmpty: false,
    },

    tooltip: {
      shared: true,
      useHTML: true,
      headerFormat: '<table>',
      pointFormat:
        '<tr><td style="font-size: 1.25cqw;">{series.name}</td><td style="text-align: right; font-size: 1.25cqw; padding-left: 10px"><b>{point.y}</b></td></tr>',
      backgroundColor: this.dark ? colors.Dark : colors.LightGrey,
      style: {
        color: this.dark ? colors.LightGrey : colors.Black,
      },
    },

    legend: {
      reversed: true,
      enabled: true,
      floating: true,
      borderWidth: this.transformPx(1),
      padding: this.transformPx(16),
      align: 'left',
      verticalAlign: 'top',
      x: 0,
      y: 0,
      borderColor: 'transparent',
      layout: 'vertical',
      itemStyle: {
        font: 'Assistant, Verdana, sans-serif',
        fontSize: '1cqw',
        fontWeight: 'normal',
        color: colors.LightGrey,
      },
      itemHoverStyle: {
        color: colors.White,
      },
    },
    plotOptions: {
      series: {
        animation: true,
        fillOpacity: 0.5,
        marker: {
          radius: 0,
          states: {
            hover: {
              radius: this.transformPx(8),
            },
          },
          symbol: 'circle',
        },
        lineWidth: this.transformPx(1),
        pointPlacement: 'on',
        point: {
          events: {
            mouseOver: this.onMouseOver,
            click: this.onClick,
          },
        },
        events: {
          mouseOut: this.onMouseOut,
        },
        showInLegend: true,
      },
    },

    series: [],
  };

  @Watch('rankings', { immediate: true, deep: true })
  onRankingsChanged() {
    if (this.rankings === undefined) {
      return;
    }
    for (const ranking of this.rankings) {
      if (!ranking) {
        return; // wait for rankings to be loaded
      }
    }

    // Lägg till grundstruktur-serie som alltid visas
    const baseStructure = {
      name: 'Grundstruktur',
      showInLegend: false,
      enableMouseTracking: false,
      data: Array(12).fill(0),
      color: 'transparent',
      zIndex: 0,
    };

    this.chartOptions.series = [baseStructure];

    for (let i = 0; i < this.overview.length; i++) {
      const ranking = this.rankings[i];
      this.chartOptions.series.unshift({
        name: this.overview[i].NAME,
        data: this.rankingValues(i),
        color: [colors.PrimaryLight, colors.Secondary, colors.Tertiary][i],
        lineColor: [colors.PrimaryLight, colors.Secondary, colors.Tertiary][i],
        lineWidth: scaleChartPx(
          3,
          this.containerWidth > 0
            ? this.containerWidth
            : CHART_WIDTH_BASELINE,
        ),
        showInLegend: true,
        enableMouseTracking: true,
        visible: true,
      });
    }
    const br = this.rankings[0].sheets[0].industryRanks;
    if (this.showBransch && br.score !== 0) {
      this.chartOptions.series.unshift({
        name: Vue.prototype.$t('Branschmedian'),
        data: [
          br.yield,
          br.operatingMargin,
          br.growth,
          br.longTerm,
          br.solidity,
          br.workingCapitalPct,
          br.financialStrength,
          br.quickRatio,
          br.interestCoverage,
          br.shortTerm,
          br.riskBuffer,
          br.returnOnCapital,
        ],
        color: colors.LightGrey,
        lineColor: colors.LightGrey,
        fillColor: 'rgba(0, 0, 0, 0.0)',
        lineWidth: scaleChartPx(
          2,
          this.containerWidth > 0
            ? this.containerWidth
            : CHART_WIDTH_BASELINE,
        ),
        dashStyle: 'longDash',
        showInLegend: true,
        enableMouseTracking: true,
        visible: true,
      });
    }
    const w =
      this.containerWidth > 0 ? this.containerWidth : CHART_WIDTH_BASELINE;
    applyMultiSeriesCompassLayout(this.chartOptions, w, this.slideLayout);
    this.reflowChart();
  }

  rankingValues(i: number) {
    if (!this.rankings[i].sheets) {
      return [];
    }
    const r = this.rankings[i].sheets[0].ranks;
    return [
      this.withLabel(r.yield),
      r.operatingMargin,
      r.growth,
      this.withLabel(r.longTerm),
      r.solidity,
      r.workingCapitalPct,
      this.withLabel(r.financialStrength),
      r.quickRatio,
      r.interestCoverage,
      this.withLabel(r.shortTerm),
      r.riskBuffer,
      r.returnOnCapital,
    ];
  }

  withLabel(v: any) {
    if (!this.showLabels) {
      return v;
    }
    return {
      dataLabels: {
        enabled: true,
        align: 'center',
        backgroundColor: colors.Primary,
        color: colors.White,
        allowOverlap: true,
        style: {
          textOutline: false,
          fontSize: '1cqw',
          fontWeight: 'bold',
        },
        verticalAlign: 'middle',
      },
      y: v,
    };
  }

  @Watch('containerWidth', { immediate: true })
  syncChartSpacing() {
    const w =
      this.containerWidth > 0 ? this.containerWidth : CHART_WIDTH_BASELINE;
    applyMultiSeriesCompassLayout(this.chartOptions, w, this.slideLayout);
    this.reflowChart();
  }

  @Watch('slideLayout')
  onSlideLayoutChanged() {
    this.syncChartSpacing();
  }

  reflowChart() {
    this.$nextTick(() => {
      const slot = this.$refs.chartSlot as HTMLElement;
      const chart = this.$refs.chart as any;
      applyPolarCompassChartSize(
        chart && chart.chart,
        this.chartOptions,
        slot,
        this.slideLayout,
      );
    });
  }

  mounted() {
    this.reflowChart();
  }

  created() {
    this.component = this;
  }

  onMouseOver(e: any) {
    this.hovered = e.target.index;
    this.component.$emit('hover', KeyValue.ByIndex(this.hovered as number));
  }

  onMouseOut() {
    this.hovered = null;
    this.component.$emit('hover', null);
  }

  onClick(e: any) {
    if (!this.hovered || this.hovered % 3 === 0) {
      return;
    }
    this.component.$emit('clicked');
  }

  get cursor() {
    if (!this.hovered || this.hovered % 3 === 0) {
      return 'default';
    } else {
      return 'pointer';
    }
  }

  transformPx(value: any) {
    const width =
      this.containerWidth > 0 ? this.containerWidth : CHART_WIDTH_BASELINE;
    if (value * (width / CHART_WIDTH_BASELINE) < 1) {
      return 1;
    }
    return value * (width / CHART_WIDTH_BASELINE);
  }
}
